import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back/index'
import { useCurrencySelection } from '@/contexts/AppStateContext'
import Ripple from '@/components/Ripple'
import { Popup, Toast } from 'react-vant'
export default React.memo(function SelectNetwork() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { selectedCurrency, actions } = useCurrencySelection()
  const [showGuidePopup, setShowGuidePopup] = useState(false)
  const [showFullGuide, setShowFullGuide] = useState(false)
  const [guideStep, setGuideStep] = useState(0)

  // 引导弹窗步骤配置
  const guideSteps = [
    {
      image: '/images/home/network-step1.png',
      title: t('什么是加密货币网络？'),
      descriptions: [t('加密货币网络描述')],
    },
    {
      image: '/images/home/network-step2.png',
      title: t('我应该使用哪个网络？'),
      descriptions: [t('网络选择描述1'), t('网络选择描述2')],
    },
    {
      image: '/images/home/network-step3.png',
      title: t('如何选择其他网络？'),
      descriptions: [t('选择网络描述')],
      showLearnMore: true,
    },
  ]

  const handleGuideNext = () => {
    if (guideStep < guideSteps.length - 1) {
      setGuideStep(guideStep + 1)
    } else {
      setShowFullGuide(false)
      setGuideStep(0)
    }
  }

  const handleCloseFullGuide = () => {
    setShowFullGuide(false)
    setGuideStep(0)
  }

  // 检查是否有选中的币种，如果没有则跳转到选择币种页面
  useEffect(() => {
    if (!selectedCurrency) {
      console.warn('未找到选中的币种信息，请先选择币种')
      navigate('/recharge/select-currency', { replace: true })
    }
  }, [selectedCurrency, navigate])


  const handleSelectNetwork = useCallback((currentNetwork: any) => {
    // 使用AppStateContext存储选中的网络信息
    actions.setCurrentNetwork({
      chainId: currentNetwork.chain_id || '',
      chainName: currentNetwork.chainName || currentNetwork.symbol || '',
      symbol: currentNetwork.symbol || ''
    })
    // 选择网络后跳转到货币详情页面，使用 replace 避免重复历史记录
    navigate(`/recharge/currency-details`, { replace: true })
  }, [actions, navigate])

  // 直接渲染网络列表
  const NetworkList = useMemo(
    () => selectedCurrency?.chainList.map((item, index) => (
      <Ripple key={index} className='rounded-2 w-full'>
        <div
          key={index}
          onClick={() => handleSelectNetwork(item)}
          className="w-full my-4 "
        >
          <div className="flex items-center">
            <div className="w-7 h-7 rounded-full mr-3 bg-gray-100 flex items-center justify-center">
              <img className='w-full w-full object-cover' src={item.icon_url} alt={item.symbol} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-base font-medium text-gray-900 mb-1">
                {item.symbol}
              </div>
              <div className="text-xs text-[#989697] mb-1">
                {t('最小充币额')}:10 {selectedCurrency?.symbol}
              </div>
              <div className="text-xs text-[#989697]">
                {t('预计约X分钟内到账', { minutes: 7 })}
              </div>
            </div>
          </div>
        </div>
      </Ripple>
    )),
    [selectedCurrency, handleSelectNetwork, t]
  )

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={
          <div className="text-3.75 font-medium text-black truncate flex items-center">
            <img className='w-6 h-6 mr-2' src={selectedCurrency?.icon} />{t('选择网络')}
          </div>
        }
      ></Header>
      <div className="bg-[#F3F3F3] rounded-lg p-4 mx-6 my-6 flex">
        <div className='w-4 h-4 bg-black text-2.5 rounded-full mr-3 text-white flex justify-center items-center'>i</div>
        <div className="flex-1">
          <div className="text-sm font-normal text-black mb-1">
            {t('不清楚如何选择正确的充币网络')}
          </div>
          <div className="text-xs text-black mb-2">
            {t('请选择与您发送资产平台上一致的网络进行充币')}
          </div>
          <div
            className="flex items-center font-bold text-black text-xs cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              setShowGuidePopup(true)
            }}
          >
            {t('了解更多')}
            <img className='ml-2' src="/icon/more.svg" alt="" />
          </div>
        </div>
      </div>

      {/* Network List */}
      <div className="px-6 pb-8">
        {NetworkList}
      </div>
      {/* 充币网络指南弹窗 */}
      <Popup
        visible={showGuidePopup}
        onClose={() => setShowGuidePopup(false)}
        position="bottom"
        round
        closeable
        closeIcon={<img src="/icon/close.svg" alt="" />}
        closeIconPosition="top-right"
        style={{ margin: '0 5% 5%', width: '90%', borderRadius: '16px' }}
      >
        <div className="px-5 pt-5 pb-5">
          <div className="text-4.5 font-semibold text-black mb-4">
            {t('如何选择正确的充币网络？')}
          </div>
          <div className="text-3.5 text-[#667085] leading-relaxed mb-4">
            {t('充币网络指南内容1')}
          </div>
          <div className="text-3.5 text-[#667085] leading-relaxed mb-4">
            {t('充币网络指南内容2')}
          </div>
          <div
            className="text-sm text-[var(--primary-color)] font-medium cursor-pointer"
            onClick={() => {
              setShowGuidePopup(false)
              setGuideStep(0)
              setShowFullGuide(true)
            }}
          >
            {t('了解更多充币知识')}
          </div>
        </div>
      </Popup>

      {/* 充币网络全屏引导弹窗 */}
      <Popup
        visible={showFullGuide}
        onClose={handleCloseFullGuide}
        position="bottom"
        style={{ height: '100%' }}
      >
        <div className="flex flex-col h-full">
          {/* 插图区域 - 占屏幕50%高度 */}
          <div className="flex flex-col items-center bg-[#F5F7FA]" style={{ height: '40vh' }}>

            <div className='w-full relative flex justify-center items-center mt-10'>
              <div className="flex gap-2 mb-10" style={{ width: '40%' }}>
                {guideSteps.map((_, index) => (
                  <div
                    key={index}
                    className="h-1.5 rounded-full flex-1 transition-colors duration-300"
                    style={{
                      backgroundColor: index === guideStep ? '#1A1A1A' : '#E5E5E5',
                    }}
                  />
                ))}

                <div className='absolute right-5 -top-2.5'
                onClick={handleCloseFullGuide}>
                  <img src="/icon/close-line.svg" alt="" />
                </div>
              </div>

            </div>
            <img
              src={guideSteps[guideStep].image}
              alt=""
              className="max-w-[80%] max-h-[40vh] object-contain"
            />
          </div>

          {/* 文字内容区域 */}
          <div className="flex-1 px-5 pt-6 pb-4 overflow-y-auto">
            <h2 className="text-5 font-medium text-[#1A1A1A] mb-4">
              {guideSteps[guideStep].title}
            </h2>
            {guideSteps[guideStep].descriptions.map((desc, i) => (
              <p key={i} className="text-4 text-[#1D2129] leading-relaxed mb-4">
                {desc}
              </p>
            ))}
            {guideSteps[guideStep].showLearnMore && (
              <p className="text-4 text-[#1D2129]">
                {t('还有其他问题？')}
                <span
                  className="text-[var(--primary-color)] cursor-pointer"
                  onClick={() => {
                    // 跳转到帮助页面或外部链接
                    Toast.info(t('即将上线'))
                  }}
                >
                  {t('了解更多')}
                </span>
              </p>
            )}
          </div>

          {/* 底部按钮 */}
          <div className="px-5 pb-8 pt-2">
            <button
              className="w-full py-3.5 rounded-full text-white font-medium text-base"
              style={{ backgroundColor: 'var(--primary-color)' }}
              onClick={handleGuideNext}
            >
              {guideStep < guideSteps.length - 1 ? t('下一步') : t('确定')}
            </button>
          </div>
        </div>
      </Popup>
    </PageLayout>
  )
})